import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { BookingDetails } from "../bookingCalendarApi"
import { isPlaceholderEmail } from "../lib/attendeeContact"
import { longDateLabel, minutesToLabel, zonedDateAndMinutes } from "../lib/siteTime"
import { AppointmentFooterSlot } from "./AppointmentDialogFooter"
import { CancelAction, ReleaseAction } from "./actions/CancelAction"
import { ReassignAction } from "./actions/ReassignAction"
import type { BookingAction, BookingActionContext, BookingActionResult, ReleaseNotes } from "./actions/types"

/**
 * The writes a row offers, in the order their buttons are drawn: the same actions the appointment
 * dialog arms, each with its own form. Moving the appointment comes first because that is what
 * somebody working through a departing host's appointments is usually here to do. Releasing the slot
 * (AB#769) is the cancellation with canned notes, and is offered only where the list supplies them.
 */
export const AFFECTED_BOOKING_ACTIONS: readonly BookingAction[] = [ReassignAction, ReleaseAction, CancelAction]

/** Which of the actions a list offers, beyond what each action decides for itself. */
export interface AffectedBookingOptions {
  /**
   * Offer moving to another host. False for a site closure (AB#769): everybody it could go to is
   * inside the closure too. True by default.
   */
  offerReassign?: boolean
}

/**
 * What an action may read about one row's booking, built from the booking alone (AB#714). Reassign and
 * cancel need nothing else — the reassign form asks the server who could take it when it opens — so a
 * list never makes a read per row. Without the CMS request id, a cancellation does not drop the notes
 * cache; nothing in a list shows notes.
 */
export function affectedBookingContext(
  booking: BookingDetails,
  timeZone: string,
  canManage: boolean,
  now: Date = new Date(),
  releaseNotes?: ReleaseNotes,
): BookingActionContext {
  const attendee = booking.attendees[0]

  return {
    booking,
    uid: booking.uid,
    timeZone,
    canManage,
    hasStarted: new Date(booking.start).getTime() <= now.getTime(),
    attendeeName: attendee?.name,
    emailIsPlaceholder: isPlaceholderEmail(attendee?.email?.trim()),
    ...(releaseNotes ? { releaseNotes } : {}),
  }
}

/** The actions a row offers to the person looking. */
export function availableActions(ctx: BookingActionContext, options: AffectedBookingOptions = {}): BookingAction[] {
  const offerReassign = options.offerReassign ?? true
  return AFFECTED_BOOKING_ACTIONS.filter(action =>
    (offerReassign || action !== ReassignAction) && action.isAvailable(ctx))
}

/** Why a row offers nothing, when it does not; undefined when it offers something. */
export function noActionReason(ctx: BookingActionContext, options: AffectedBookingOptions = {}): string | undefined {
  if (availableActions(ctx, options).length > 0) return undefined
  if (!ctx.canManage) return "Changing appointments needs appointments:manage."
  if (ctx.hasStarted) return "Under way: record its outcome from the calendar instead."
  return "It can no longer be changed."
}

/** "Wednesday, Oct 14" and "1:00 PM – 1:15 PM", on the site's clock. */
export function bookingWhen(booking: Pick<BookingDetails, "start" | "end">, timeZone: string): { date: string; time: string } {
  const start = zonedDateAndMinutes(booking.start, timeZone)
  const end = booking.end ? zonedDateAndMinutes(booking.end, timeZone) : null

  return {
    date: longDateLabel(start.date),
    time: end ? `${minutesToLabel(start.minutes)} – ${minutesToLabel(end.minutes)}` : minutesToLabel(start.minutes),
  }
}

/** The words a row's result badge shows. */
export const RESULT_LABELS: Record<BookingActionResult["kind"], string> = {
  done: "Done",
  released: "Released",
  cancelled: "Cancelled",
  reassigned: "Moved",
  failed: "Failed",
}

/** A write that has ended on a row, and the booking as it was listed when it was made. */
export interface HandledRow {
  booking: BookingDetails
  result: BookingActionResult
}

/** One row of the table. */
export interface AffectedRow {
  booking: BookingDetails
  result?: BookingActionResult
  /** Still in the list the server last sent: an appointment there is still Booked and may be acted on. */
  live: boolean
}

/**
 * The rows to draw: every booking the server listed, and every row handled here since — kept, with its
 * result, after the refetch that drops it, so each row shows how it ended — soonest first.
 */
export function affectedRows(bookings: readonly BookingDetails[], handled: Readonly<Record<string, HandledRow>>): AffectedRow[] {
  const listed = new Set(bookings.map(booking => booking.uid))
  const rows: AffectedRow[] = bookings.map(booking => ({ booking, result: handled[booking.uid]?.result, live: true }))

  for (const [uid, row] of Object.entries(handled)) {
    if (!listed.has(uid)) rows.push({ booking: row.booking, result: row.result, live: false })
  }

  return rows.sort((a, b) => new Date(a.booking.start).getTime() - new Date(b.booking.start).getTime())
}

/** A row's result: what happened, and for a failure, why — the booking itself has not changed. */
export function ResultBadge({ result }: { result: BookingActionResult }) {
  const failed = result.kind === "failed"

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant={failed ? "destructive" : "secondary"}>{RESULT_LABELS[result.kind]}</Badge>
      {failed && result.detail && <span className="max-w-xs text-xs text-red-700">{result.detail}</span>}
    </div>
  )
}

export interface AffectedBookingsListProps extends AffectedBookingOptions {
  /** The appointments, in the calendar's shape, in the order to list them. */
  bookings: readonly BookingDetails[]
  timeZone: string
  /** Holds `appointments:manage`: offers reassign and cancel on appointments not yet started. */
  canManage: boolean
  /** Shown instead of the table when there are none. */
  emptyMessage?: string
  /** Fixes "now" for the started check; the present by default. */
  now?: Date
  /**
   * The notes releasing a booking's slot starts with (AB#769). Given, each row offers "Release slot";
   * left out, it does not.
   */
  releaseNotes?: (booking: BookingDetails) => ReleaseNotes
}

/**
 * A list of appointments somebody has to deal with — each with Move to another host and Cancel, and
 * Release slot where the caller supplies its notes — built to be reused (AB#714's staff move; AB#769's
 * quick edit). It takes bookings as the calendar reads them and needs nothing per row: each action's
 * form fetches what it needs when opened, and the cancel and reassign mutations invalidate the
 * bookings cache, so a list drawn from a cached query is asked for again once one lands. Each row
 * shows how its write ended, and keeps showing it after that refetch drops the booking.
 */
export function AffectedBookingsList({
  bookings,
  timeZone,
  canManage,
  emptyMessage,
  now,
  releaseNotes,
  offerReassign,
}: AffectedBookingsListProps) {
  const [armed, setArmed] = useState<{ action: BookingAction; ctx: BookingActionContext } | null>(null)
  const [busy, setBusy] = useState(false)
  const [handled, setHandled] = useState<Record<string, HandledRow>>({})

  const options: AffectedBookingOptions = { offerReassign }
  const rows = affectedRows(bookings, handled)

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">
        {emptyMessage ?? "No appointments."}
      </div>
    )
  }

  const close = () => {
    setArmed(null)
    setBusy(false)
  }

  // Recorded against the booking the write was armed on, so the row keeps its result once the
  // refetch has dropped it.
  const recordResult = (result: BookingActionResult) => {
    if (!armed) return
    const booking = armed.ctx.booking
    setHandled(current => ({ ...current, [booking.uid]: { booking, result } }))
  }

  const ArmedForm = armed?.action.Form
  const armedWhen = armed ? bookingWhen(armed.ctx.booking, timeZone) : null

  return (
    <div className="rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Appointment</TableHead>
            <TableHead>Attendee</TableHead>
            <TableHead>Reference</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ booking, result, live }) => {
            const ctx = affectedBookingContext(booking, timeZone, canManage, now, releaseNotes?.(booking))

            // A row that has been dealt with offers nothing more; one whose write failed is unchanged,
            // so it offers the same again while the server still lists it.
            const settled = result !== undefined && result.kind !== "failed"
            const actions = live && !settled ? availableActions(ctx, options) : []
            const when = bookingWhen(booking, timeZone)
            const attendee = booking.attendees[0]
            const reason = live && !settled ? noActionReason(ctx, options) : undefined

            return (
              <TableRow key={booking.uid}>
                <TableCell>
                  <div className="font-medium">{when.date}</div>
                  <div className="text-sm text-muted-foreground">{when.time}</div>
                </TableCell>
                <TableCell>
                  <div>{attendee?.name || "—"}</div>
                  {attendee?.phoneNumber && (
                    <div className="text-sm text-muted-foreground">{attendee.phoneNumber}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{booking.metadata?.referenceCode ?? "—"}</TableCell>
                <TableCell className="text-right">
                  {result && <ResultBadge result={result} />}
                  {actions.length > 0 ? (
                    <div className={`flex flex-wrap justify-end gap-2${result ? " mt-2" : ""}`}>
                      {actions.map(action => (
                        <Button
                          key={action.id}
                          size="sm"
                          variant={action.triggerVariant}
                          onClick={() => setArmed({ action, ctx })}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  ) : reason ? (
                    <span className="text-sm text-muted-foreground">{reason}</span>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {/* The armed write, in a dialog of its own, as the appointment dialog stacks it. Not dismissed
          while its write is in flight; either way it closes back to the list. */}
      {armed && ArmedForm && armedWhen && (
        <Dialog open onOpenChange={(open) => { if (!open && !busy) close() }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{armed.action.label}</DialogTitle>
              <DialogDescription>
                {[armed.ctx.attendeeName, `${armedWhen.date}, ${armedWhen.time}`].filter(Boolean).join(" · ")}
              </DialogDescription>
            </DialogHeader>
            <AppointmentFooterSlot.Provider value={null}>
              <ArmedForm
                key={armed.ctx.uid}
                ctx={armed.ctx}
                onDone={close}
                onClose={close}
                onBusyChange={setBusy}
                onResult={recordResult}
              />
            </AppointmentFooterSlot.Provider>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
